"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { Button } from '@open-mercato/ui/primitives/button'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { SendObjectMessageDialog } from '@open-mercato/ui/backend/messages'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { updateCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { LeaveRequestForm, buildLeaveRequestPayload, type LeaveRequestFormValues } from '@open-mercato/core/modules/staff/components/LeaveRequestForm'
import { type LeaveRequestsResponse, type NormalizedLeaveRequest, normalizeLeaveRequest, resolveStatusVariant, formatDateLabel, formatDateRange } from '../../../../lib/leaveRequestHelpers'

export default function StaffMyLeaveRequestDetailPage({ params }: { params?: { id?: string } }) {
  const id = params?.id
  const t = useT()
  const router = useRouter()
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [record, setRecord] = React.useState<NormalizedLeaveRequest | null>(null)

  React.useEffect(() => {
    if (!id) {
      setError(t('staff.leaveRequests.errors.notFound', 'Leave request not found.'))
      setIsLoading(false)
      return
    }
    const requestId = id
    let cancelled = false
    async function load() {
      setIsLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams({ page: '1', pageSize: '1', ids: requestId })
        const payload = await readApiResultOrThrow<LeaveRequestsResponse>(
          `/api/staff/leave-requests?${params.toString()}`,
          undefined,
          { errorMessage: t('staff.leaveRequests.errors.load', 'Failed to load leave request.') },
        )
        const entry = Array.isArray(payload.items) ? payload.items[0] : null
        if (!entry) throw new Error(t('staff.leaveRequests.errors.notFound', 'Leave request not found.'))
        if (!cancelled) {
          setRecord(normalizeLeaveRequest(entry))
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : t('staff.leaveRequests.errors.load', 'Failed to load leave request.')
          setError(message)
          setRecord(null)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [id, t])

  const status = record?.status ?? 'pending'
  const memberLabel = record?.member?.displayName ?? null
  const initialValues = React.useMemo<LeaveRequestFormValues>(() => ({
    id: record?.id,
    memberId: record?.memberId ?? null,
    memberLabel,
    startDate: record?.startDate ?? null,
    endDate: record?.endDate ?? null,
    timezone: record?.timezone ?? null,
    unavailabilityReasonEntryId: record?.unavailabilityReasonEntryId ?? null,
    unavailabilityReasonValue: record?.unavailabilityReasonValue ?? null,
    note: record?.note ?? null,
  }), [record, memberLabel])
  const dateSummary = formatDateRange(record?.startDate, record?.endDate)
const handleSubmit = React.useCallback(async (values: LeaveRequestFormValues) => {
    if (!record?.id) return
    const payload = buildLeaveRequestPayload(values, { id: record.id })
    await updateCrud('staff/leave-requests', payload, {
      errorMessage: t('staff.leaveRequests.form.errors.update', 'Failed to update leave request.'),
    })
    flash(t('staff.leaveRequests.form.flash.updated', 'Leave request updated.'), 'success')
    router.push('/backend/staff/my-leave-requests')
  }, [record?.id, router, t])

  if (isLoading) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('staff.leaveRequests.form.loading', 'Loading leave request...')} />
        </PageBody>
      </Page>
    )
  }

  if (error || !record) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage label={error ?? t('staff.leaveRequests.errors.load', 'Failed to load leave request.')} />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageBody>
        <div className="mb-6 space-y-2 rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={resolveStatusVariant(status)}>
              {t(`staff.leaveRequests.status.${status}`, status)}
            </Badge>
            {record.decidedAt ? (
              <span className="text-xs text-muted-foreground">
                {t('staff.leaveRequests.decision.at', 'Decision at')} {formatDateLabel(record.decidedAt)}
              </span>
            ) : null}
          </div>
          {record.decisionComment ? (
            <div className="text-sm text-muted-foreground">
              <div className="font-medium text-foreground">{t('staff.leaveRequests.decision.comment', 'Decision comment')}</div>
              <p>{record.decisionComment}</p>
            </div>
          ) : null}
        </div>

        {status === 'pending' ? (
          <LeaveRequestForm
            title={t('staff.leaveRequests.form.editTitle', 'Leave request')}
            submitLabel={t('staff.leaveRequests.form.actions.save', 'Save')}
            backHref="/backend/staff/my-leave-requests"
            cancelHref="/backend/staff/my-leave-requests"
            initialValues={initialValues}
            onSubmit={handleSubmit}
            allowMemberSelect={false}
            memberLabel={memberLabel}
            extraActions={record.id ? (
              <SendObjectMessageDialog
                object={{
                  entityModule: 'staff',
                  entityType: 'leave_request',
                  entityId: record.id,
                  sourceEntityType: 'staff:leave_request',
                  sourceEntityId: record.id,
                  previewData: {
                    title: memberLabel || t('staff.leaveRequests.messages.contextTitle', 'Linked leave request'),
                    subtitle: dateSummary || undefined,
                    status: record?.status ?? undefined,
                  },
                }}
                viewHref={`/backend/staff/leave-requests/${record.id}`}
                lockedType="staff.leave_request_approval"
                requiredActionConfig={{
                  mode: 'required',
                  options: [
                    { id: 'approve', label: t('staff.notifications.leaveRequest.actions.approve', 'Approve') },
                    { id: 'reject', label: t('staff.notifications.leaveRequest.actions.reject', 'Reject') },
                  ],
                }}
                defaultValues={{
                  type: 'staff.leave_request_approval',
                  subject: t('staff.leaveRequests.messages.compose.subject', 'Leave request approval needed'),
                  body: t('staff.leaveRequests.messages.compose.body', 'Please review this leave request and take action.'),
                }}
              />
            ) : null}
          />
        ) : (
          <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
            <div className="font-medium text-foreground">{t('staff.leaveRequests.detail.summary', 'Request details')}</div>
            <p>{memberLabel ? t('staff.leaveRequests.detail.member', 'Team member') + `: ${memberLabel}` : null}</p>
            <p>{t('staff.leaveRequests.detail.dates', 'Dates')}: {formatDateRange(record.startDate, record.endDate)}</p>
            {record.unavailabilityReasonValue ? (
              <p>{t('staff.leaveRequests.detail.reason', 'Reason')}: {record.unavailabilityReasonValue}</p>
            ) : null}
            {record.note ? <p>{t('staff.leaveRequests.detail.note', 'Note')}: {record.note}</p> : null}
          </div>
        )}
      </PageBody>
    </Page>
  )
}
